import { supabase } from '../../lib/supabase';

export type InventoryMovementType = 'entry' | 'consumption' | 'adjustment_in' | 'adjustment_out';

export interface StoreInventoryItem {
  id: number;
  store_id: number;
  name: string;
  description: string | null;
  category: string | null;
  unit: string;
  quantity: number;
  minimum_quantity: number | null;
  cost: number | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface StoreInventoryMovement {
  id: number;
  inventory_item_id: number;
  movement_type: InventoryMovementType;
  quantity: number;
  reason: string | null;
  notes: string | null;
  created_by: number | null;
  created_at: string;
  item?: {
    id: number;
    name: string;
    unit: string;
    category: string | null;
  } | null;
  creator?: {
    id: number;
    username?: string;
    fullname?: string;
  } | null;
}

export interface InventoryStats {
  totalItems: number;
  lowStockItems: number;
  outOfStockItems: number;
  totalInventoryValue: number;
  categoriesCount: number;
}

export interface MovementPayload {
  p_item_id: number;
  p_movement_type: InventoryMovementType;
  p_quantity: number;
  p_reason?: string | null;
  p_notes?: string | null;
}

/**
 * Busca todos os itens do inventário de uma loja
 */
export const getStoreInventoryItems = async (
  storeId: number,
  options?: {
    search?: string;
    category?: string;
    statusFilter?: 'all' | 'low_stock' | 'out_of_stock' | 'normal' | 'inactive';
  }
): Promise<StoreInventoryItem[]> => {
  try {
    let query = supabase
      .from('store_inventory_items')
      .select('*')
      .eq('store_id', storeId)
      .order('name', { ascending: true });

    if (options?.statusFilter === 'inactive') {
      query = query.eq('active', false);
    } else {
      query = query.eq('active', true);
    }

    if (options?.category && options.category !== 'all') {
      query = query.eq('category', options.category);
    }

    if (options?.search && options.search.trim()) {
      query = query.ilike('name', `%${options.search.trim()}%`);
    }

    const { data, error } = await query;
    if (error) throw error;

    let items = (data || []).map(item => ({
      ...item,
      quantity: Number(item.quantity) || 0,
      minimum_quantity: item.minimum_quantity !== null && item.minimum_quantity !== undefined ? Number(item.minimum_quantity) : null,
      cost: item.cost !== null && item.cost !== undefined ? Number(item.cost) : null,
    })) as StoreInventoryItem[];

    // Filtros adicionais de estoque pós-query
    if (options?.statusFilter === 'low_stock') {
      items = items.filter(
        i => i.minimum_quantity !== null && i.quantity <= i.minimum_quantity && i.quantity > 0
      );
    } else if (options?.statusFilter === 'out_of_stock') {
      items = items.filter(i => i.quantity <= 0);
    } else if (options?.statusFilter === 'normal') {
      items = items.filter(
        i => (i.minimum_quantity === null || i.quantity > i.minimum_quantity) && i.quantity > 0
      );
    }

    return items;
  } catch (error) {
    console.error('Error fetching store inventory items:', error);
    throw error;
  }
};

/**
 * Cria um novo item no inventário
 */
export const createStoreInventoryItem = async (
  itemData: {
    store_id: number;
    name: string;
    description?: string | null;
    category?: string | null;
    unit?: string;
    quantity?: number;
    minimum_quantity?: number | null;
    cost?: number | null;
    active?: boolean;
    initial_reason?: string;
    initial_notes?: string;
  }
): Promise<StoreInventoryItem> => {
  try {
    const initialQty = Number(itemData.quantity) || 0;

    // Inserimos o item com quantidade 0 se formos registrar via RPC, ou direto com a quantidade
    const insertPayload = {
      store_id: itemData.store_id,
      name: itemData.name.trim(),
      description: itemData.description?.trim() || null,
      category: itemData.category?.trim() || null,
      unit: itemData.unit?.trim() || 'un',
      quantity: 0,
      minimum_quantity: itemData.minimum_quantity !== undefined && itemData.minimum_quantity !== null ? Number(itemData.minimum_quantity) : null,
      cost: itemData.cost !== undefined && itemData.cost !== null ? Number(itemData.cost) : null,
      active: itemData.active !== undefined ? itemData.active : true,
    };

    const { data: createdItem, error: createError } = await supabase
      .from('store_inventory_items')
      .insert(insertPayload)
      .select()
      .single();

    if (createError) throw createError;

    // Se informou quantidade inicial maior que 0, registra a primeira movimentação via RPC
    if (initialQty > 0 && createdItem) {
      try {
        const { data: updatedAfterMovement, error: rpcError } = await supabase.rpc(
          'register_inventory_movement',
          {
            p_item_id: createdItem.id,
            p_movement_type: 'entry',
            p_quantity: initialQty,
            p_reason: itemData.initial_reason || 'Estoque inicial',
            p_notes: itemData.initial_notes || 'Cadastro do item no almoxarifado'
          }
        );

        if (!rpcError && updatedAfterMovement) {
          return {
            ...updatedAfterMovement,
            quantity: Number(updatedAfterMovement.quantity) || 0,
            minimum_quantity: updatedAfterMovement.minimum_quantity !== null ? Number(updatedAfterMovement.minimum_quantity) : null,
            cost: updatedAfterMovement.cost !== null ? Number(updatedAfterMovement.cost) : null
          };
        }
      } catch (rpcErr) {
        console.warn('Initial movement RPC warning, falling back to direct update:', rpcErr);
        // Fallback: se o RPC falhar por auth ou similar, atualiza a quantidade direto
        const { data: fallbackItem } = await supabase
          .from('store_inventory_items')
          .update({ quantity: initialQty })
          .eq('id', createdItem.id)
          .select()
          .single();

        if (fallbackItem) {
          return {
            ...fallbackItem,
            quantity: Number(fallbackItem.quantity) || 0,
            minimum_quantity: fallbackItem.minimum_quantity !== null ? Number(fallbackItem.minimum_quantity) : null,
            cost: fallbackItem.cost !== null ? Number(fallbackItem.cost) : null
          };
        }
      }
    }

    return {
      ...createdItem,
      quantity: Number(createdItem.quantity) || 0,
      minimum_quantity: createdItem.minimum_quantity !== null ? Number(createdItem.minimum_quantity) : null,
      cost: createdItem.cost !== null ? Number(createdItem.cost) : null
    };
  } catch (error) {
    console.error('Error creating store inventory item:', error);
    throw error;
  }
};

/**
 * Cria itens em lote no inventário da loja (usado para catálogo pré-definido ou importação)
 */
export const createBatchStoreInventoryItems = async (
  storeId: number,
  itemsToCreate: Array<{
    name: string;
    description?: string | null;
    category?: string | null;
    unit?: string;
    quantity?: number;
    minimum_quantity?: number | null;
    cost?: number | null;
    active?: boolean;
    initial_reason?: string;
  }>
): Promise<{
  created: StoreInventoryItem[];
  skipped: string[];
  errors: string[];
}> => {
  const created: StoreInventoryItem[] = [];
  const skipped: string[] = [];
  const errors: string[] = [];

  try {
    // 1. Busca nomes existentes para evitar duplicidade de itens
    const { data: existingRecords } = await supabase
      .from('store_inventory_items')
      .select('name')
      .eq('store_id', storeId);

    const existingNames = new Set(
      (existingRecords || []).map(r => r.name.toLowerCase().trim())
    );

    // 2. Itera sobre cada item
    for (const item of itemsToCreate) {
      const cleanName = item.name.trim();
      if (!cleanName) continue;

      if (existingNames.has(cleanName.toLowerCase())) {
        skipped.push(cleanName);
        continue;
      }

      try {
        const newItem = await createStoreInventoryItem({
          store_id: storeId,
          name: cleanName,
          description: item.description || null,
          category: item.category || null,
          unit: item.unit || 'un',
          quantity: item.quantity !== undefined ? Number(item.quantity) : 0,
          minimum_quantity: item.minimum_quantity !== undefined && item.minimum_quantity !== null ? Number(item.minimum_quantity) : null,
          cost: item.cost !== undefined && item.cost !== null ? Number(item.cost) : null,
          active: item.active !== undefined ? item.active : true,
          initial_reason: item.initial_reason || 'Cadastro via catálogo pré-definido',
          initial_notes: 'Item adicionado em lote ao inventário'
        });

        created.push(newItem);
        existingNames.add(cleanName.toLowerCase());
      } catch (itemErr: any) {
        console.error(`Error creating item "${cleanName}":`, itemErr);
        errors.push(`${cleanName}: ${itemErr.message || 'Falha ao salvar'}`);
      }
    }

    return { created, skipped, errors };
  } catch (err: any) {
    console.error('Error in createBatchStoreInventoryItems:', err);
    throw err;
  }
};

/**
 * Atualiza os metadados cadastrais de um item (nome, categoria, unidade, custo, estoque mínimo, etc.)
 */
export const updateStoreInventoryItem = async (
  itemId: number,
  updates: Partial<{
    name: string;
    description: string | null;
    category: string | null;
    unit: string;
    minimum_quantity: number | null;
    cost: number | null;
    active: boolean;
  }>
): Promise<StoreInventoryItem> => {
  try {
    const payload: any = {
      ...updates,
      updated_at: new Date().toISOString()
    };

    if (payload.name) payload.name = payload.name.trim();
    if (payload.category !== undefined) payload.category = payload.category?.trim() || null;
    if (payload.unit) payload.unit = payload.unit.trim();
    if (payload.minimum_quantity !== undefined) {
      payload.minimum_quantity = payload.minimum_quantity === null || payload.minimum_quantity === '' ? null : Number(payload.minimum_quantity);
    }
    if (payload.cost !== undefined) {
      payload.cost = payload.cost === null || payload.cost === '' ? null : Number(payload.cost);
    }

    const { data, error } = await supabase
      .from('store_inventory_items')
      .update(payload)
      .eq('id', itemId)
      .select()
      .single();

    if (error) throw error;

    return {
      ...data,
      quantity: Number(data.quantity) || 0,
      minimum_quantity: data.minimum_quantity !== null ? Number(data.minimum_quantity) : null,
      cost: data.cost !== null ? Number(data.cost) : null
    };
  } catch (error) {
    console.error('Error updating store inventory item:', error);
    throw error;
  }
};

/**
 * Registra movimentação de estoque chamando a função RPC `register_inventory_movement`
 */
export const registerInventoryMovement = async (
  payload: MovementPayload
): Promise<StoreInventoryItem> => {
  try {
    const { data, error } = await supabase.rpc('register_inventory_movement', {
      p_item_id: Number(payload.p_item_id),
      p_movement_type: payload.p_movement_type,
      p_quantity: Number(payload.p_quantity),
      p_reason: payload.p_reason?.trim() || null,
      p_notes: payload.p_notes?.trim() || null
    });

    if (error) {
      console.error('register_inventory_movement RPC error:', error);
      throw error;
    }

    return {
      ...data,
      quantity: Number(data.quantity) || 0,
      minimum_quantity: data.minimum_quantity !== null ? Number(data.minimum_quantity) : null,
      cost: data.cost !== null ? Number(data.cost) : null
    };
  } catch (error) {
    console.error('Error in registerInventoryMovement:', error);
    throw error;
  }
};

/**
 * Busca histórico de movimentações (filtrável por item e/ou loja)
 */
export const getStoreInventoryMovements = async (
  storeId: number,
  options?: {
    itemId?: number;
    limit?: number;
  }
): Promise<StoreInventoryMovement[]> => {
  try {
    const limit = options?.limit || 50;

    let query = supabase
      .from('store_inventory_movements')
      .select(`
        *,
        item:store_inventory_items!inner(id, name, unit, category, store_id)
      `)
      .eq('item.store_id', storeId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (options?.itemId) {
      query = query.eq('inventory_item_id', options.itemId);
    }

    const { data, error } = await query;
    if (error) throw error;

    return (data || []).map(m => ({
      ...m,
      quantity: Number(m.quantity) || 0,
      item: m.item || null
    })) as StoreInventoryMovement[];
  } catch (error) {
    console.error('Error fetching store inventory movements:', error);
    throw error;
  }
};

/**
 * Desativa ou remove um item do inventário
 */
export const toggleActiveStoreInventoryItem = async (
  itemId: number,
  active: boolean
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('store_inventory_items')
      .update({ active, updated_at: new Date().toISOString() })
      .eq('id', itemId);

    if (error) throw error;
  } catch (error) {
    console.error('Error toggling active status on store inventory item:', error);
    throw error;
  }
};

/**
 * Remove fisicamente o item (caso não possua movimentações restritivas)
 */
export const deleteStoreInventoryItem = async (itemId: number): Promise<void> => {
  try {
    const { error } = await supabase
      .from('store_inventory_items')
      .delete()
      .eq('id', itemId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting store inventory item:', error);
    throw error;
  }
};
